import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { Changeset } from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import { validatePresence } from 'ember-changeset-validations/validators';
import { restartableTask, type Task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import CollectionViewComponent from 'volta/components/collection-view';
import CollectionView, {
  CollectionViewResourceName,
  DEFAULT_COLLECTION_VIEW_ID
} from 'volta/models/collection-view';
import JsonApiInfinityModel from 'volta/models/jsonapi-infinity-model';
import { normalize } from 'volta/utils/api/serialize-and-push';
import { indexBy, indexByFn } from 'volta/utils/array-utils';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import {
  collectionFiltersQueryParamsToQueryParams,
  collectionSortByFromQueryParams,
  collectionViewFromQueryParams,
  collectionViewToQueryParams,
  filterValuesFromQueryParams,
  getFiltersFromQueryParams,
  inFilter,
serializeFilterValues
} from 'volta/utils/filters-utils';
import { deepEqual, getChangesetValue } from 'volta/utils/object-utils';

import type { CreateRecordProperties } from '@ember-data/store/-private/store-service';
import type EmberArray from '@ember/array';
import type Owner from '@ember/owner';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import type { WithBoundArgs } from '@glint/template';
import type { BufferedChangeset } from 'ember-changeset/types';
import type Infinity from 'ember-infinity/services/infinity';
import type { IntlService } from 'ember-intl';
import type { EmberTableSort, IBvTableColumn } from 'volta/components/bv-table';
import type { IResourceName } from 'volta/models/base-model';
import type {
  ICollectionArgs,
  ICollectionFilter,
  ICollectionGroupOption,
  ICollectionProperty,
  ICollectionSort,
  ICollectionSortOption,
  ICollectionViewDefinition,
  ICollectionViewEditCmd,
  ICreateCollectionViewCmd,
  IPOWorkbenchCollectionArgs,
  TCollectionType
} from 'volta/models/types/collection-view';
import type { TEntityType } from 'volta/models/types/entity-types';
import type BvFlashService from 'volta/services/bv-flash';
import type SessionUserService from 'volta/services/session-user';
import type StoreService from 'volta/services/store';
import type { IJsonApiQuery } from 'volta/utils/api/jsonapi-types';
import type { TError } from 'volta/utils/error-utils';

export const DEFAULT_PAGE_SIZE = 50;

export const EMPTY_VIEW: ICollectionViewEditCmd = {
  name: '',
  description: '',
  pageSize: DEFAULT_PAGE_SIZE,
  isTemplate: false,
  args: {},
  groupBy: [],
  sortBy: [],
  showForUsers: [],
  showForRoles: []
};

const VIEW_CREATION_VALIDATOR = {
  name: validatePresence(true)
};

const VIEW_EDITION_VALIDATOR = VIEW_CREATION_VALIDATOR;

export const COLLECTION_VIEW_QP_KEYS = [
  'v',
  'sortBy',
  'page',
  'pageSize',
  'filters',
  'groupBy',
  'args',
  'collectionType'
];

export interface TPublicApi {
  collectionDefinition: ICollectionViewDefinition;
  componentArgs?: ICollectionArgs;
  fetchJsonApi: Task<void | never[] | EmberArray<any>, [apiQuery?: any]>;
  filters: ICollectionFilter[];
  infiniteScroll?: boolean;
  jsonApiQueryParams: TQueryParams;
  metadata?: object;
  pageSize?: number;
  properties: (IBvTableColumn & ICollectionProperty)[];
  resourceName: IResourceName;
  selectedView?: CollectionView<ICollectionArgs>;
  sorts: EmberTableSort[];
  views: CollectionView[];
  collectionType: TCollectionType;
  onExportCurrent: () => void;
  onExportSelected: (selectedIds: string[]) => void;
  onFilterValuesChange: (filter: TFilterValue, field: string) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onPropertyReorder: (
    col1: IBvTableColumn,
    col2: IBvTableColumn,
    columns: IBvTableColumn[]
  ) => void;
  onPropertySort: (sorts: EmberTableSort[]) => void;
  setComponentArgs: (settings: ICollectionArgs) => void;
}

export interface CollectionViewApiSignature {
  Args: {
    collectionDefinition: ICollectionViewDefinition;
    queryParams: TQueryParams;
    resourceName: IResourceName;
    infiniteScroll?: boolean;
    metadata?: object;
    fixedGroups?: string[];
    enableGrouping?: boolean;
    enableAttributes?: boolean;
    onFetchJsonApi?: (response: EmberArray<any>) => void;
    formatApiQuery?: (query: IJsonApiQuery) => IJsonApiQuery;
    onComponentArgsChange?: (
      args: ICollectionArgs,
      changeset: BufferedChangeset & ICollectionViewEditCmd
    ) => void;
    onQueryParamsChange?: (queryParams: TQueryParams) => void;
  };
  Blocks: {
    default: [
      TPublicApi,
      WithBoundArgs<
        typeof CollectionViewComponent,
        | 'collectionDefinition'
        | 'createViewChangeset'
        | 'editViewChangeset'
        | 'enableGrouping'
        | 'groupByOptions'
        | 'hasUnsavedChanges'
        | 'isLoading'
        | 'metadata'
        | 'onCancelCreation'
        | 'onComponentArgsChange'
        | 'onCreate'
        | 'onDelete'
        | 'onDiscard'
        | 'onDuplicate'
        | 'onFilterValuesChange'
        | 'onGroupByChange'
        | 'onGroupByOptionsReorder'
        | 'onInitCreate'
        | 'onPropertyChange'
        | 'onQueryParamsChange'
        | 'onSave'
        | 'onSearch'
        | 'onSortDirectionChange'
        | 'onSortOptionsReorder'
        | 'onSortSelectionChange'
        | 'onCollectionTypeChange'
        | 'queryParams'
        | 'selectedView'
        | 'selectedViewId'
        | 'selectView'
        | 'sendCollectionQueryParams'
        | 'sortOptions'
        | 'sorts'
        | 'views'
      >
    ];
  };
}

export default class CollectionViewApi extends Component<CollectionViewApiSignature> {
  // Services
  // ~~~~~~

  @service store!: StoreService;
  @service sessionUser!: SessionUserService;
  @service intl!: IntlService;
  @service bvFlash!: BvFlashService;
  @service router!: RouterService;
  @service infinity!: Infinity;

  // Tracked Properties
  // ~~~~~~

  @tracked selectedViewId: string = DEFAULT_COLLECTION_VIEW_ID;
  @tracked views: CollectionView[] = [];
  @tracked createViewChangeset?: BufferedChangeset & ICreateCollectionViewCmd;
  @tracked editViewChangeset?: BufferedChangeset & ICollectionViewEditCmd;
  @tracked createViewCmd?: ICreateCollectionViewCmd;
  @tracked editViewCmd?: ICollectionViewEditCmd;
  @tracked sortOptions: ICollectionSortOption[] = [];
  @tracked groupByOptions: ICollectionGroupOption[] = [];
  @tracked sorts: EmberTableSort[] = [];
  @tracked viewProperties: (IBvTableColumn & ICollectionProperty)[] = [];
  @tracked filters: ICollectionFilter[] = [];
  @tracked pageSize: number = DEFAULT_PAGE_SIZE;
  @tracked componentArgs?: ICollectionArgs;
  @tracked collectionType: TCollectionType =
    this.args.collectionDefinition.defaultView.collectionType;

  // Getters
  // ~~~~~~

  get orderedViews(): CollectionView[] {
    const { views } = this;
    const { collectionDefinition } = this.args;
    if (!views) {
      return [];
    }
    const { collectionViewsOrder } = this.sessionUser.preferences ?? {};
    const order = collectionViewsOrder?.[collectionDefinition.resourceName.plural as TEntityType];
    const viewsWithNoUserPreferences = order
      ? views.filter(
          (v) =>
            !collectionViewsOrder?.[
              collectionDefinition.resourceName.plural as TEntityType
            ]?.includes(v.idOrDefault)
        )
      : views;

    const viewsWithUserPreferences =
      (order
        ?.map((id) => views.find((v) => v.idOrDefault === id))
        .filter(Boolean) as CollectionView[]) ?? [];

    return [...viewsWithUserPreferences, ...viewsWithNoUserPreferences];
  }

  @cached
  get indexedColumnDefs() {
    return indexByFn(this.args.collectionDefinition.definitions, (d) => d.property ?? d.valuePath);
  }

  get entityType() {
    const { defaultView } = this.args.collectionDefinition;
    return defaultView.entityType;
  }

  @cached
  get hasDefaultView() {
    return !!this.views.find((v) => !v.id);
  }

  @cached
  get viewsById() {
    return new Map(this.views.map((v) => [v.idOrDefault, v]));
  }

  @cached
  get selectedView() {
    return this.viewsById.get(this.selectedViewId);
  }

  get hasUnsavedChanges() {
    return (this.editViewChangeset?.changes.length ?? 0) > 0 && !this.createViewChangeset;
  }

  @cached
  get jsonApiQueryParams() {
    const defs = this.indexedColumnDefs;
    const { page, pageSize, filters, sortBy, groupBy } = this.args.queryParams ?? {};

    const propKey = (prop?: IBvTableColumn, isSort?: boolean) =>
      (isSort ? prop?.sortKey : undefined) ?? prop?.property ?? prop?.valuePath;

    const qp: TQueryParams = {
      page,
      pageSize,
      groupBy,
      sort: collectionSortByFromQueryParams(sortBy)
        .map((s) => `${s.asc ? '' : '-'}${propKey(defs[s.property], true)}`)
        .join(',')
    };

    const filtersQp = collectionFiltersQueryParamsToQueryParams(filters);
    return { ...qp, ...filtersQp };
  }

  @cached
  get publicApi(): TPublicApi {
    return {
      jsonApiQueryParams: this.jsonApiQueryParams,
      selectedView: this.selectedView,
      views: this.orderedViews,
      sorts: this.sorts,
      filters: this.filters,
      properties: this.viewProperties,
      pageSize: this.pageSize,
      componentArgs: this.componentArgs,
      collectionDefinition: this.args.collectionDefinition,
      onPropertySort: this.handlePropertySort,
      onPropertyReorder: this.handlePropertyReorder,
      onFilterValuesChange: this.handleFilterValuesChange,
      onPageSizeChange: this.handlePageSizeChange,
      onPageChange: this.handlePageChange,
      onExportCurrent: this.exportCurrent,
      onExportSelected: this.exportSelected,
      setComponentArgs: this.handleComponentArgsChange,
      fetchJsonApi: this.fetchJsonApi,
      resourceName: this.args.resourceName,
      infiniteScroll: this.args.infiniteScroll,
      metadata: this.args.metadata,
      collectionType: this.collectionType
    };
  }

  // Lifecycle Events
  // ~~~~~~

  constructor(owner: Owner, args: CollectionViewApiSignature['Args']) {
    super(owner, args);
    this.fetchViews.perform(true);
  }

  // Actions
  // ~~~~~~

  @action
  handlePropertySort(sorts: EmberTableSort[]) {
    let limitedSorts = sorts;
    if (this.args.collectionDefinition.sortLimit) {
      limitedSorts = limitedSorts.slice(-this.args.collectionDefinition.sortLimit);
    }
    const toCollectionSorts = limitedSorts.map((sort) => ({
      property: sort.valuePath,
      asc: sort.isAscending
    }));

    this.onSortEdit(toCollectionSorts);
  }

  @action
  handlePropertyReorder(_col1: IBvTableColumn, _col2: IBvTableColumn, columns: IBvTableColumn[]) {
    const propsByPath = indexBy([...(this.editViewChangeset?.properties ?? [])], 'property');
    const propColumns = columns
      .map((col) => col.valuePath && propsByPath[col.valuePath])
      .filter(Boolean) as ICollectionProperty[];
    this.handlePropertyChange(propColumns);
  }

  @action
  handlePropertyChange(props: ICollectionProperty[]) {
    if (this.editViewChangeset) {
      this.editViewChangeset.set('properties', [...props]);
      this.rollbackPropIfPristine('properties');
      this.refreshSortOptions();
      this.refreshViewProperties();
      this.sendCollectionQueryParams();
    }
  }

  @action
  handleComponentArgsChange(settings: ICollectionArgs) {
    if (this.editViewChangeset) {
      this.editViewChangeset.set('args', { ...settings });
      this.componentArgs = { ...settings };
      this.rollbackPropIfPristine('args');
      this.args.onComponentArgsChange?.(settings, this.editViewChangeset);
      this.refreshGroupByOptions();
      this.refreshSortOptions();
      this.refreshViewProperties();
      this.sendCollectionQueryParams();
    }
  }

  @action
  handleCollectionTypeChange(type: TCollectionType) {
    if (this.editViewChangeset) {
      this.editViewChangeset.set('collectionType', type);
      this.collectionType = type;
      this.rollbackPropIfPristine('collectionType');
      this.sendCollectionQueryParams();
    }
  }

  @action
  handleQueryParamsChange(_el: HTMLElement, [queryParams]: [TQueryParams]) {
    const { v } = queryParams ?? {};

    if (!v && this.orderedViews[0]) {
      this.selectedViewId = this.orderedViews[0].idOrDefault;
      this.initEditChangeset();
    } else if (v && this.selectedViewId !== v) {
      this.selectedViewId = this.viewsById.get(v) ? v : DEFAULT_COLLECTION_VIEW_ID;
      this.initEditChangeset();
    }
    this.fuseCollectionWithQueryParams();
  }

  @action
  handleFilterValuesChange(filter: TFilterValue, field: string) {
    if (!this.editViewChangeset) {
      return;
    }

    const filters = this.editViewChangeset.get('filters')?.slice() ?? [];
    const formattedFilter = {
      property: field,
      comparator: filter.comparator,
      values: filter.values
    };
    const filterIndex = filters.findIndex((f: ICollectionFilter) => f.property === field);

    if (filterIndex === -1) {
      filters.push(formattedFilter);
    } else if (formattedFilter.values.length === 0) {
      filters.splice(filterIndex, 1);
    } else {
      filters[filterIndex] = formattedFilter;
    }

    this.editViewChangeset.set('filters', [...filters]);
    this.filters = [...filters];
    this.rollbackPropIfPristine('filters');
    this.sendCollectionQueryParams();
  }

  @action
  handleSearch(_: TFilterValues, search: string) {
    return this.handleFilterValuesChange(
      {
        comparator: 'lk',
        values: [search],
        enabled: true
      },
      'q'
    );
  }

  @action
  selectView(viewId: string) {
    if (this.selectedViewId === viewId || this.views.length === 0) {
      return;
    }

    this.selectedViewId = viewId;
    this.initEditChangeset();
    this.sendCollectionQueryParams();
  }

  @action
  handleCancelCreation() {
    this.createViewCmd = undefined;
    this.createViewChangeset = undefined;
  }

  @action
  handleDiscard() {
    this.initEditChangeset();
    this.sendCollectionQueryParams();
  }

  @action
  fuseCollectionWithQueryParams() {
    const serialised = this.getSerialisedCollectionView();

    if (serialised && this.editViewChangeset) {
      Object.entries(serialised).forEach(([key, value]) => {
        if (key === 'id' || !value) {
          return;
        }
        const csValue = getChangesetValue(this.editViewChangeset!, key);

        if (!deepEqual(csValue, value)) {
          this.editViewChangeset!.set(key, value);
        }
      });
      this.syncPublicApiState();
    }
  }

  @action
  handlePageSizeChange(pageSize: number) {
    if (this.editViewChangeset) {
      this.editViewChangeset.set('pageSize', pageSize);
      this.pageSize = pageSize;
      this.rollbackPropIfPristine('pageSize');
      this.sendCollectionQueryParams();
    }
  }

  @action
  handlePageChange(page: number) {
    if (this.args.onQueryParamsChange) {
      this.args.onQueryParamsChange({ ...(this.args.queryParams ?? {}), page });
      return;
    }
    const { queryParams = {} } = this.args;
    this.router.transitionTo({ queryParams: { ...queryParams, page } });
  }

  @action
  sendCollectionQueryParams(isInitial: boolean = false): Transition | undefined {
    try {
      const { queryParams = {}, collectionDefinition } = this.args;

      const cs = this.editViewChangeset;
      if (!cs) {
        return;
      }

      const cv = collectionViewToQueryParams(
        {
          id: this.selectedViewId,
          filters: cs.get('filters'),
          sortBy: cs.get('sortBy'),
          groupBy: cs.get('groupBy'),
          pageSize: cs.get('pageSize'),
          args: cs.get('args'),
          collectionType: cs.get('collectionType')
        },
        queryParams,
        collectionDefinition
      );

      if (this.args.onQueryParamsChange) {
        this.args.onQueryParamsChange(cv);
        return;
      }

      const hasParams = Object.values(queryParams).some(Boolean);

      return isInitial || !hasParams
        ? this.router.replaceWith(this.router.currentRouteName, { queryParams: cv })
        : this.router.transitionTo({ queryParams: cv });
    } catch (error) {
      defaultErrorHandler(error as TError);
      return;
    }
  }

  @action
  getDefaultView(attrs: object = {}): CollectionView {
    const { defaultView } = this.args.collectionDefinition;
    return this.store.createRecord<CollectionView>('collection-view', {
      ...EMPTY_VIEW,
      ...defaultView,
      ...attrs,
      id: null
    } as CreateRecordProperties<CollectionView>);
  }

  @action
  handleInitCreate() {
    this.initCreateChangeset();
  }

  @action
  handleSortSelectionChange(sort: ICollectionSortOption, checked: boolean) {
    const { sortLimit } = this.args.collectionDefinition;
    let count = 0;

    const selectedOptions: ICollectionSortOption[] = [];
    const unseledtedOptions: ICollectionSortOption[] = [];

    this.sortOptions.forEach((o) => {
      const aboveLimit = sortLimit && count >= sortLimit;
      const multiple = () => (aboveLimit ? false : sort.value === o.value ? checked : o.selected);
      const selected = sortLimit === 1 ? (sort.value === o.value ? checked : false) : multiple();
      const option = {
        ...o,
        selected,
        asc: selected ? o.asc : true
      };

      if (selected) {
        count++;
        selectedOptions.push(option);
      } else {
        unseledtedOptions.push(option);
      }
    });
    this.onSortEdit(
      CollectionViewApi.fromSortOptionsToSorts([...selectedOptions, ...unseledtedOptions])
    );
  }

  @action
  handleGroupByChange(group: ICollectionGroupOption, checked: boolean) {
    const { groupLimit } = this.args.collectionDefinition;
    let count = 0;

    const selectedOptions: ICollectionGroupOption[] = [];
    const unseledtedOptions: ICollectionGroupOption[] = [];

    this.groupByOptions.forEach((o) => {
      const aboveLimit = groupLimit && count >= groupLimit;
      const multiple = () => (aboveLimit ? false : group.value === o.value ? checked : o.selected);
      const selected = groupLimit === 1 ? (group.value === o.value ? checked : false) : multiple();
      const option = {
        ...o,
        selected
      };

      if (selected) {
        count++;
        selectedOptions.push(option);
      } else {
        unseledtedOptions.push(option);
      }
    });
    this.onGroupEdit(
      CollectionViewApi.fromGroupOptionsToGroupBy([...selectedOptions, ...unseledtedOptions])
    );
  }

  @action
  handleSortDirectionChange(sort: ICollectionSortOption) {
    const { sortLimit } = this.args.collectionDefinition;
    let count = 0;
    const options = this.sortOptions.map((o) => {
      const aboveLimit = sortLimit != null && sortLimit > 0 && count >= sortLimit;
      const multiple = () =>
        aboveLimit ? false : sort.value === o.value && !sort.selected ? true : o.selected;
      const selected = sortLimit === 1 ? sort.value === o.value : multiple();

      const asc = aboveLimit || sort.value !== o.value ? o.asc : !(o.asc ?? true);

      if (selected) {
        count++;
      }

      return {
        ...o,
        selected,
        asc
      };
    });
    this.onSortEdit(CollectionViewApi.fromSortOptionsToSorts(options));
  }

  @action
  handleSortOptionsReorder(sortOptions: ICollectionSortOption[]) {
    this.onSortEdit(CollectionViewApi.fromSortOptionsToSorts(sortOptions));
  }

  @action
  handleGroupByOptionsReorder(options: ICollectionGroupOption[]) {
    this.onGroupEdit(CollectionViewApi.fromGroupOptionsToGroupBy(options));
  }

  @action
  exportCurrent() {
    return this.export();
  }

  @action
  exportSelected(selectedIds: string[]) {
    return this.export(selectedIds);
  }

  private export(selectedIds: string[] = []) {
    const properties: string[] = [];
    this.viewProperties.forEach((p) => {
      if (p.property) {
        properties.push(p.property);
      }
    });

    properties.push(...(this.editViewChangeset?.groupBy ?? []));
    const filterValues = filterValuesFromQueryParams(this.args.queryParams['filters']);
    const body = selectedIds.length
      ? { filter: { id: inFilter(selectedIds) } }
      : {
          filter: {
            ...getFiltersFromQueryParams(
              serializeFilterValues(filterValues, this.args.collectionDefinition.filters),
              this.args.collectionDefinition.filters
            )
          }
        };

    const resourceName = this.args.resourceName?.plural;
    if (resourceName) {
      return this.store.downloadFile(
        `${resourceName}/export/${this.selectedViewId ?? DEFAULT_COLLECTION_VIEW_ID}/data.csv`,
        'POST',
        { properties: properties.join(',') },
        body
      );
    }

    return;
  }

  // Tasks
  // ~~~~~~~~

  fetchJsonApi = restartableTask(async (apiQuery = {}) => {
    try {
      const { infiniteScroll, resourceName, formatApiQuery } = this.args;
      if (!resourceName) {
        return [];
      }

      const query = formatApiQuery?.(apiQuery) ?? apiQuery;
      const response: EmberArray<any> = infiniteScroll
        ? await this.infinity.model(resourceName.singular, query, JsonApiInfinityModel)
        : await this.store.query(resourceName.singular, query);
      this.args.onFetchJsonApi?.(response);
      return response;
    } catch (error) {
      return defaultErrorHandler(error as TError);
    }
  });

  fetchViews = restartableTask(async (isInitial: boolean = false) => {
    const { entityType } = this.args.collectionDefinition.defaultView;
    try {
      const data = await this.store.rawQuery('collection-view', ['mine', entityType as string], {
        include: 'showForUsers,showForRoles'
      });

      const defaultView = this.getDefaultView();
      const views = normalize('collection-view', data) as CollectionView[];
      this.views = [defaultView, ...(views ?? [])];
      const { id } = this.getSerialisedCollectionView() ?? {};
      const vId =
        (id === 'default' ? this.orderedViews[0]?.idOrDefault : id) ?? DEFAULT_COLLECTION_VIEW_ID;

      this.selectedViewId = this.viewsById.get(vId)?.idOrDefault ?? DEFAULT_COLLECTION_VIEW_ID;

      this.initEditChangeset();
      this.fuseCollectionWithQueryParams();
      this.refreshSortOptions();
      this.refreshGroupByOptions();
      this.refreshViewProperties();
      this.sendCollectionQueryParams(isInitial);
    } catch (error) {
      defaultErrorHandler(error as TError);
      this.bvFlash.error(
        this.intl.t('bvList.query.error', { resourceNamePlural: this.intl.t('skus') })
      );
    }
  });

  createView = restartableTask(async () => {
    const cs = this.createViewChangeset;
    try {
      if (!cs) {
        throw new Error('Cannot create a view without a changeset');
      }

      cs.validate();

      if (cs.isValid) {
        cs.execute();

        const newView = await CollectionView.createCollectionView(this.createViewCmd!);
        this.views = [...this.views, newView];
        this.selectedViewId = newView.id!;

        this.handleDiscard();
        this.handleCancelCreation();
        this.bvFlash.success(
          this.intl.t('bvList.singleActions.createSuccess', {
            resourceNameSingular: this.intl.t(CollectionViewResourceName.singular),
            name: newView.name
          })
        );
      }
    } catch (error) {
      defaultErrorHandler(error as TError);
      if (this.createViewChangeset) {
        this.bvFlash.error(
          this.intl.t('bvList.singleActions.createFail', {
            resourceNameSingular: this.intl.t(CollectionViewResourceName.singular),
            name: this.createViewChangeset.get('name')
          })
        );
      }
    }
  });

  editView = restartableTask(async () => {
    const selectedView = this.selectedView;

    if (!selectedView || !selectedView?.id || selectedView.isLocked) {
      await this.duplicateFromSelectedView.perform();
    } else {
      const cs = this.editViewChangeset;
      try {
        if (!cs) {
          throw new Error('Cannot edit a view without a changeset');
        }
        await cs.validate();

        if (cs.isValid) {
          cs.execute();

          if (
            Object.keys(cs.change).some((change) =>
              ['isTemplate', 'showForUsers', 'showForRoles'].includes(change)
            )
          ) {
            await selectedView.shareCollectionView({
              isTemplate: this.editViewCmd!.isTemplate ?? false,
              showForRoles: this.editViewCmd!.showForRoles ?? [],
              showForUsers: this.editViewCmd!.showForUsers ?? []
            });
          }
          const cmd = this.editViewCmd;

          await selectedView.updateCollectionView(cmd);
        }

        this.initEditChangeset();
        this.sendCollectionQueryParams();
        this.bvFlash.success(
          this.intl.t('bvList.singleActions.updateSuccess', {
            resourceNameSingular: this.intl.t(CollectionViewResourceName.singular),
            name: selectedView?.name
          })
        );
      } catch (error) {
        defaultErrorHandler(error as TError);
        this.bvFlash.error(
          this.intl.t('bvList.singleActions.updateFail', {
            resourceNameSingular: this.intl.t(CollectionViewResourceName.singular),
            name: cs?.get('name') ?? 'N/A'
          })
        );
        return;
      }
    }
  });

  deleteView = restartableTask(async () => {
    const { selectedView } = this;
    const idToDelete = selectedView?.id;
    if (!idToDelete) {
      return;
    }
    try {
      await selectedView.deleteCollectionView();
      const defaultView = this.getDefaultView();
      this.views = this.views.reduce<CollectionView[]>(
        (acc, v) => {
          if (v.id && v.id !== idToDelete) {
            acc.push(v);
          }
          return acc;
        },
        [defaultView]
      );

      this.selectedViewId = this.orderedViews[0]?.idOrDefault ?? DEFAULT_COLLECTION_VIEW_ID;

      this.bvFlash.success(
        this.intl.t('bvList.singleActions.deleteSuccess', {
          resourceNameSingular: this.intl.t(CollectionViewResourceName.singular),
          name: selectedView.name
        })
      );
      this.initEditChangeset();
      this.sendCollectionQueryParams();
    } catch (error) {
      defaultErrorHandler(error as TError);
      this.bvFlash.error(
        this.intl.t('bvList.singleActions.deleteFail', {
          resourceNameSingular: this.intl.t(CollectionViewResourceName.singular),
          name: selectedView.name
        })
      );
      return;
    }
  });

  duplicateFromSelectedView = restartableTask(async () => {
    const cs = this.editViewChangeset;
    try {
      if (!cs) {
        throw new Error('Cannot duplicate a view without a changeset');
      }
      await cs.validate();

      if (cs.isValid) {
        cs.execute();
        const name = this.selectedView?.id
          ? `${this.editViewCmd!.name} (${this.intl.t('copySingular')})`
          : this.editViewCmd!.name;
        const cmd = {
          ...this.selectedView?.toDuplicateCmd(),
          ...this.editViewCmd,
          name,
          id: undefined
        } as ICreateCollectionViewCmd;

        if (this.views.length === 0 || !cmd) {
          throw new Error(
            'View should not be empty. The temporary view should at least be present.'
          );
        }

        const newView = await CollectionView.createCollectionView(cmd);
        const views = [...this.views, newView];
        views.splice(0, 1, this.getDefaultView());
        this.views = views;
        this.selectedViewId = newView.id!;
        this.initEditChangeset();
        this.fuseCollectionWithQueryParams();
        this.sendCollectionQueryParams();
        return newView;
      }
    } catch (error) {
      defaultErrorHandler(error as TError);
      this.bvFlash.error(
        this.intl.t('bvList.singleActions.updateFail', {
          resourceNameSingular: this.intl.t(CollectionViewResourceName.singular),
          name: cs?.get('name') ?? 'N/A'
        })
      );
      return;
    }
    return;
  });

  // Private functions
  // ~~~~~~

  private onSortEdit(sorts: ICollectionSort[]) {
    if (this.editViewChangeset) {
      this.editViewChangeset.set('sortBy', [...sorts]);
      this.rollbackPropIfPristine('sortBy');
      this.refreshSortOptions();
      this.sendCollectionQueryParams();
    }
  }

  private onGroupEdit(groupBy: string[]) {
    if (this.editViewChangeset) {
      this.editViewChangeset.set('groupBy', [...groupBy]);
      this.rollbackPropIfPristine('groupBy');
      this.refreshGroupByOptions();
      this.sendCollectionQueryParams();
    }
  }

  private async initCreateChangeset() {
    this.createViewCmd = this.getDefaultView({ name: '', description: '' }).toCreateCmd();
    this.createViewChangeset = <BufferedChangeset & ICreateCollectionViewCmd>(
      Changeset(
        this.createViewCmd,
        lookupValidator(VIEW_CREATION_VALIDATOR),
        VIEW_CREATION_VALIDATOR
      )
    );
  }

  private initEditChangeset() {
    if (!this.selectedView) {
      return;
    }

    this.editViewCmd = { ...this.selectedView.toUpdateCmd(), ...this.selectedView.toShareCmd() };
    this.editViewChangeset = <BufferedChangeset & ICollectionViewEditCmd>(
      Changeset(this.editViewCmd, lookupValidator(VIEW_EDITION_VALIDATOR), VIEW_EDITION_VALIDATOR)
    );
    this.syncPublicApiState();
    this.args.onComponentArgsChange?.(
      this.editViewChangeset.get('args') as IPOWorkbenchCollectionArgs,
      this.editViewChangeset
    );
    this.refreshSortOptions();
    this.refreshGroupByOptions();
    this.refreshViewProperties();
  }

  private getSerialisedCollectionView() {
    const { queryParams, collectionDefinition } = this.args;

    try {
      if (queryParams) {
        return collectionViewFromQueryParams(this.args.queryParams, collectionDefinition);
      }
      return undefined;
    } catch (error) {
      defaultErrorHandler(error as TError);
      return undefined;
    }
  }

  private rollbackPropIfPristine(propPath: string) {
    if (
      this.editViewChangeset &&
      deepEqual(
        this.editViewChangeset.get(propPath),
        this.editViewChangeset.get(`data.${propPath}`)
      )
    ) {
      this.editViewChangeset.rollbackProperty(propPath);
    }
    return this.editViewChangeset;
  }

  private refreshGroupByOptions() {
    const groupBy: string[] = this.editViewChangeset?.get('groupBy') ?? [];
    const { fixedGroups = [] } = this.args;

    const defs = this.indexedColumnDefs;
    const options: ICollectionGroupOption[] = [];

    groupBy.forEach((gb) => {
      const maybeGroup = defs[gb];
      if (
        maybeGroup &&
        maybeGroup?.valuePath &&
        (maybeGroup.isGroupable || maybeGroup.isCustomField)
      ) {
        options.push({
          label: maybeGroup.columnName ?? maybeGroup.valuePath,
          value: maybeGroup.valuePath,
          color: maybeGroup.color,
          selected: true,
          editable: !fixedGroups.includes(maybeGroup.valuePath)
        });
      }
    });

    Object.values(defs).forEach((s) => {
      if (s.valuePath && !groupBy.includes(s.valuePath) && (s.isGroupable || s.isCustomField)) {
        options.push({
          label: s.columnName ?? s.valuePath,
          value: s.valuePath,
          color: s.color,
          selected: false,
          editable: !fixedGroups.includes(s.valuePath)
        });
      }
    });

    this.groupByOptions = options;
  }

  private refreshSortOptions() {
    const sortBy: ICollectionSort[] = this.editViewChangeset?.get('sortBy') ?? [];
    const indexedSortBy = indexByFn(sortBy, (s) => s.property);
    const indexedProps = indexByFn(
      (this.editViewChangeset?.get('properties') ?? []) as ICollectionProperty[],
      (p) => p.property
    );
    const { sorts } = this.args.collectionDefinition;
    const indexedSorts = indexByFn(sorts, (s) => s.origin);
    const defs = this.indexedColumnDefs;
    const options: ICollectionSortOption[] = [];
    const newSorts: EmberTableSort[] = [];

    sortBy.forEach((sb) => {
      if (indexedProps[sb.property]) {
        const maybeSort = indexedSorts[sb.property];
        if (maybeSort) {
          options.push({
            ...maybeSort,
            selected: true,
            asc: indexedSortBy[sb.property]?.asc ?? true
          });
        }
      }

      newSorts.push({
        valuePath: sb.property,
        sortKey: defs[sb.property]?.sortKey ?? sb.property,
        isAscending: sb.asc
      });
    });

    sorts.forEach((s) => {
      if (indexedProps[s.origin] && !indexedSortBy[s.origin]) {
        options.push({
          ...s,
          selected: false,
          asc: true
        });
      }
    });

    this.sortOptions = options;
    this.sorts = newSorts;
  }

  private refreshViewProperties() {
    const defs = this.indexedColumnDefs;
    const properties: ICollectionProperty[] = this.editViewChangeset?.get('properties') ?? [];

    this.viewProperties = (properties ?? [])
      .map(
        (column) =>
          ({
            ...defs[column.property],
            ...column
          }) as IBvTableColumn & ICollectionProperty
      )
      .filter((c) => c.valuePath != null);
  }

  private syncPublicApiState() {
    if (!this.editViewChangeset) {
      return;
    }

    const filters = (this.editViewChangeset.get('filters') ?? []) as ICollectionFilter[];
    const pageSize = (this.editViewChangeset.get('pageSize') ?? DEFAULT_PAGE_SIZE) as number;
    const args = this.editViewChangeset.get('args') as ICollectionArgs | undefined;
    const collectionType =
      (this.editViewChangeset.get('collectionType') as TCollectionType | undefined) ??
      this.selectedView?.collectionType ??
      this.args.collectionDefinition.defaultView.collectionType;

    this.filters = [...filters];
    this.pageSize = pageSize;
    this.componentArgs = args ? { ...args } : undefined;
    this.collectionType = collectionType;
  }

  // Static functions

  private static fromSortOptionsToSorts(options: ICollectionSortOption[]) {
    return options.reduce<ICollectionSort[]>((acc, o) => {
      if (o.selected) {
        acc.push({
          property: o.origin,
          asc: o.asc ?? true
        });
      }
      return acc;
    }, []);
  }

  private static fromGroupOptionsToGroupBy(options: ICollectionGroupOption[]) {
    return options.reduce<string[]>((acc, o) => {
      if (o.selected) {
        acc.push(o.value);
      }
      return acc;
    }, []);
  }

  <template>
    {{yield
      this.publicApi
      (component
        CollectionViewComponent
        collectionDefinition=@collectionDefinition
        createViewChangeset=this.createViewChangeset
        editViewChangeset=this.editViewChangeset
        enableAttributes=@enableAttributes
        enableGrouping=@enableGrouping
        groupByOptions=this.groupByOptions
        hasUnsavedChanges=this.hasUnsavedChanges
        isLoading=this.fetchViews.isRunning
        metadata=@metadata
        onCancelCreation=this.handleCancelCreation
        onCollectionTypeChange=this.handleCollectionTypeChange
        onComponentArgsChange=this.handleComponentArgsChange
        onCreate=(perform this.createView)
        onDelete=(perform this.deleteView)
        onDiscard=this.handleDiscard
        onDuplicate=(perform this.duplicateFromSelectedView)
        onFilterValuesChange=this.handleFilterValuesChange
        onGroupByChange=this.handleGroupByChange
        onGroupByOptionsReorder=this.handleGroupByOptionsReorder
        onInitCreate=this.handleInitCreate
        onPropertyChange=this.handlePropertyChange
        onQueryParamsChange=this.handleQueryParamsChange
        onSave=(perform this.editView)
        onSearch=this.handleSearch
        onSortDirectionChange=this.handleSortDirectionChange
        onSortOptionsReorder=this.handleSortOptionsReorder
        onSortSelectionChange=this.handleSortSelectionChange
        queryParams=@queryParams
        selectedView=this.selectedView
        selectedViewId=this.selectedViewId
        selectView=this.selectView
        sendCollectionQueryParams=this.sendCollectionQueryParams
        sortOptions=this.sortOptions
        sorts=this.sorts
        views=this.orderedViews
      )
    }}
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'CollectionView::Api': typeof CollectionViewApi;
    'collection-view/api': typeof CollectionViewApi;
  }
}
